/**
 * A short piece of writing from a language model, with the keys the app already holds
 * (Settings: a Venice key, a Google key, an OpenAI key, kept for the session only). Venice
 * serves many models through one door (Claude, GPT, Gemini, Grok, Kimi, DeepSeek, Qwen,
 * Llama, its own), so with a Venice key the model is a choice; the Google and OpenAI keys
 * each give one model. Used by the Ideas page's writing tools (components/ideas).
 */
import { GoogleGenAI } from '@google/genai';
import { thinkingAllowance, veniceChat, type VeniceTextModel } from './venice';
import { numberedBody } from './screenplay';

export interface TextModelKeys {
    venice?: string;
    google?: string;
    openai?: string;
}

export interface TextRequest {
    /** What the model is for, in this call */
    system: string;
    prompt: string;
    /** The answer's length allowed, in tokens; a thinking model gets room to think on top (withThinking) */
    maxTokens?: number;
    temperature?: number;
    /** The model thinks before it answers: set by withThinking from the model chosen */
    reasoning?: boolean;
    /** How hard it thinks, one of the model's efforts */
    reasoningEffort?: string;
}

/** What a model answered, and what it was billed for where the service says */
export interface TextAnswer {
    text: string;
    inputTokens: number | null;
    outputTokens: number | null;
}

/** A model that can write: "provider:model" */
export type TextModelId = string;

export interface TextModelOption {
    id: TextModelId;
    name: string;
    group: 'Venice' | 'More on Venice' | 'Google' | 'OpenAI';
    /** Dollars per million tokens, where known */
    inputUsd: number | null;
    outputUsd: number | null;
    /** The model thinks before it answers (the catalog says so): the thinking costs and needs room */
    reasoning?: boolean;
    /** The efforts it can think with, and the one it takes when none is chosen */
    efforts?: string[];
    defaultEffort?: string;
}

export const GOOGLE_TEXT_MODEL = 'gemini-2.5-flash';
export const OPENAI_TEXT_MODEL = 'gpt-4o-mini';

/**
 * Well-known models on Venice, first in the list, with their prices as the catalog gave them
 * on 2026-10-06 (dollars per million tokens); the live catalog replaces these once read.
 */
export const VENICE_PICKS: { id: string; name: string; inputUsd: number; outputUsd: number }[] = [
    { id: 'claude-sonnet-5-5', name: 'Claude Sonnet 5.5', inputUsd: 2.5, outputUsd: 12.5 },
    { id: 'claude-opus-5-5', name: 'Claude Opus 5.5', inputUsd: 4.8, outputUsd: 24 },
    { id: 'claude-fable-5-1', name: 'Claude Fable 5.1', inputUsd: 12, outputUsd: 60 },
    { id: 'openai-gpt-55', name: 'GPT-5.5', inputUsd: 6.25, outputUsd: 37.5 },
    { id: 'openai-gpt-54-mini', name: 'GPT-5.4 Mini', inputUsd: 0.9375, outputUsd: 5.625 },
    { id: 'openai-gpt-4o-mini-2024-07-18', name: 'GPT-4o Mini', inputUsd: 0.1875, outputUsd: 0.75 },
    { id: 'gemini-3-8-flash', name: 'Gemini 3.8 Flash', inputUsd: 0.9375, outputUsd: 4.6875 },
    { id: 'grok-4-7', name: 'Grok 4.7', inputUsd: 2.27, outputUsd: 6.8 },
    { id: 'kimi-k3', name: 'Kimi K3', inputUsd: 3.75, outputUsd: 18.75 },
    { id: 'deepseek-v4-flash', name: 'DeepSeek V4 Flash', inputUsd: 0.138, outputUsd: 0.275 },
    { id: 'qwen-3-8-flash', name: 'Qwen 3.8 Flash', inputUsd: 0.14, outputUsd: 0.49 },
    { id: 'venice-uncensored-1-2', name: 'Venice Uncensored 1.2', inputUsd: 0.2, outputUsd: 0.9 },
    { id: 'llama-3.3-70b', name: 'Llama 3.3 70B', inputUsd: 0.7, outputUsd: 2.8 },
];

export const textModelAvailable = (keys: TextModelKeys): boolean => !!(keys.venice || keys.google || keys.openai);

export const NO_TEXT_MODEL = 'Writing tools need a Venice, Google or OpenAI key. Add one in Settings, under API keys.';

/** The models the keys at hand can ask, in the order offered; Venice's picks first, then the rest of its catalog */
export const textModelOptions = (keys: TextModelKeys, catalog: VeniceTextModel[] | null = null): TextModelOption[] => {
    const options: TextModelOption[] = [];
    if (keys.venice) {
        const live = new Map((catalog ?? []).map(model => [model.id, model]));
        const thinking = (model: VeniceTextModel | undefined) => (model?.reasoning ? { reasoning: true, efforts: model.efforts ?? [], ...(model.defaultEffort ? { defaultEffort: model.defaultEffort } : {}) } : {});
        for (const pick of VENICE_PICKS) {
            const model = live.get(pick.id);
            if (catalog && !model) continue; // no longer served
            options.push({ id: `venice:${pick.id}`, name: model?.name ?? pick.name, group: 'Venice', inputUsd: model?.inputUsd ?? pick.inputUsd, outputUsd: model?.outputUsd ?? pick.outputUsd, ...thinking(model) });
        }
        const picked = new Set(VENICE_PICKS.map(pick => pick.id));
        for (const model of [...(catalog ?? [])].filter(model => !picked.has(model.id) && !model.id.startsWith('e2ee-')).sort((a, b) => a.name.localeCompare(b.name))) {
            options.push({ id: `venice:${model.id}`, name: model.name, group: 'More on Venice', inputUsd: model.inputUsd, outputUsd: model.outputUsd, ...thinking(model) });
        }
    }
    if (keys.google) options.push({ id: `google:${GOOGLE_TEXT_MODEL}`, name: 'Gemini 2.5 Flash (your Google key)', group: 'Google', inputUsd: null, outputUsd: null });
    if (keys.openai) options.push({ id: `openai:${OPENAI_TEXT_MODEL}`, name: 'GPT-4o Mini (your OpenAI key)', group: 'OpenAI', inputUsd: null, outputUsd: null });
    return options;
};

const REMEMBERED = 'noodle.textModel';

/** The model chosen last time on this device, if the keys at hand still offer it; else the first offered */
export const chooseTextModel = (keys: TextModelKeys, options: TextModelOption[] = textModelOptions(keys)): TextModelId | null => {
    let remembered: string | null = null;
    try { remembered = localStorage.getItem(REMEMBERED); } catch { /* no storage */ }
    if (remembered && options.some(option => option.id === remembered)) return remembered;
    if (remembered?.startsWith('venice:') && keys.venice && !options.some(option => option.group === 'More on Venice')) return remembered; // the catalog is not in yet
    return options[0]?.id ?? null;
};

export const rememberTextModel = (id: TextModelId): void => {
    try { localStorage.setItem(REMEMBERED, id); } catch { /* no storage */ }
};

// ── Thinking ─────────────────────────────────────────────────────────────────

const REMEMBERED_THINKING = 'noodle.textThinking';

/** What each effort is called on a card */
export const THINKING_LABELS: Record<string, string> = { none: 'No thinking', minimal: 'Minimal', low: 'Light', medium: 'Medium', high: 'Deep', xhigh: 'Deeper', max: 'Deepest' };

/** The effort chosen last on this device, when the model offers it; else the model's own; null for a model that does not think */
export const chooseThinking = (option: Pick<TextModelOption, 'reasoning' | 'efforts' | 'defaultEffort'> | null | undefined): string | null => {
    if (!option?.reasoning) return null;
    const efforts = option.efforts ?? [];
    let remembered: string | null = null;
    try { remembered = localStorage.getItem(REMEMBERED_THINKING); } catch { /* no storage */ }
    if (remembered && (efforts.length === 0 || efforts.includes(remembered))) return remembered;
    if (option.defaultEffort && (efforts.length === 0 || efforts.includes(option.defaultEffort))) return option.defaultEffort;
    return efforts.includes('medium') ? 'medium' : efforts[0] ?? null;
};

export const rememberThinking = (effort: string): void => {
    try { localStorage.setItem(REMEMBERED_THINKING, effort); } catch { /* no storage */ }
};

/**
 * The request as it must be made of the model chosen: a thinking model is told how hard to
 * think and given room for it on top of the answer (venice.ts thinkingAllowance); any other
 * model gets the request as it is.
 */
export const withThinking = (request: TextRequest, option: Pick<TextModelOption, 'reasoning' | 'efforts' | 'defaultEffort'> | null | undefined, effort: string | null = chooseThinking(option)): TextRequest => {
    if (!option?.reasoning) return request;
    return { ...request, reasoning: true, ...(effort ? { reasoningEffort: effort } : {}) };
};

/** The name of a model, for a button or a line */
export const textModelName = (keys: TextModelKeys, id: TextModelId | null = chooseTextModel(keys), options: TextModelOption[] = textModelOptions(keys)): string | null => {
    if (!id) return null;
    const option = options.find(o => o.id === id);
    if (option) return option.name.replace(/ \(your .* key\)$/, '');
    const [provider, model] = splitId(id);
    return provider === 'venice' ? model : provider === 'google' ? 'Gemini' : 'GPT-4o mini';
};

const splitId = (id: TextModelId): [string, string] => {
    const at = id.indexOf(':');
    return at < 0 ? ['', id] : [id.slice(0, at), id.slice(at + 1)];
};

const count = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/** The model's answer as plain text, with its token counts; throws with a message a person can read */
export const generateText = async (keys: TextModelKeys, request: TextRequest, id: TextModelId | null = chooseTextModel(keys)): Promise<TextAnswer> => {
    const { system, prompt, maxTokens = 900, temperature = 0.9, reasoning, reasoningEffort } = request;
    const [provider, model] = id ? splitId(id) : ['', ''];
    if (provider === 'venice' && keys.venice) {
        return veniceChat({ apiKey: keys.venice, model, system, prompt, maxTokens, temperature, ...(reasoning ? { reasoning, reasoningEffort } : {}) });
    }
    if (provider === 'google' && keys.google) {
        const ai = new GoogleGenAI({ apiKey: keys.google });
        const response = await ai.models.generateContent({
            model: model || GOOGLE_TEXT_MODEL,
            contents: [{ role: 'user', parts: [{ text: `${system}\n\n${prompt}` }] }],
            config: { temperature, maxOutputTokens: maxTokens },
        });
        const text = response.candidates?.[0]?.content?.parts?.map((part: any) => part.text).filter(Boolean).join('').trim() || '';
        if (!text) throw new Error('The model answered nothing.');
        return { text, inputTokens: count(response.usageMetadata?.promptTokenCount), outputTokens: count(response.usageMetadata?.candidatesTokenCount) };
    }
    if (provider === 'openai' && keys.openai) {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keys.openai}` },
            body: JSON.stringify({
                model: model || OPENAI_TEXT_MODEL,
                messages: [{ role: 'system', content: system }, { role: 'user', content: prompt }],
                temperature,
                max_tokens: maxTokens,
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error?.message || `OpenAI answered ${response.status}.`);
        const text = data?.choices?.[0]?.message?.content?.trim() || '';
        if (!text) throw new Error('The model answered nothing.');
        return { text, inputTokens: count(data?.usage?.prompt_tokens), outputTokens: count(data?.usage?.completion_tokens) };
    }
    if (!textModelAvailable(keys)) throw new Error(NO_TEXT_MODEL);
    throw new Error('That model needs a key that is not there. Pick another model, or add the key in Settings.');
};

// ── What it costs ────────────────────────────────────────────────────────────

/** Tokens, roughly, in a piece of text */
export const roughTokens = (text: string): number => Math.ceil(text.length / 4);

/**
 * What a request would cost, in dollars, from the model's prices per million tokens: the
 * words sent, and about two thirds of the answer the model is allowed (it rarely uses all),
 * the room for thinking counted with the answer when the request gives it (thinking is
 * billed as output). Null when the model's prices are not known (the Google and OpenAI keys
 * bill elsewhere).
 */
export const estimateCostUsd = (option: Pick<TextModelOption, 'inputUsd' | 'outputUsd'> | null | undefined, request: TextRequest): number | null => {
    if (!option || option.inputUsd === null || option.outputUsd === null) return null;
    const input = roughTokens(`${request.system}\n\n${request.prompt}`);
    const allowed = (request.maxTokens ?? 900) + (request.reasoning ? thinkingAllowance(request.reasoningEffort) : 0);
    const output = Math.round(allowed * 0.66);
    return (input * option.inputUsd + output * option.outputUsd) / 1_000_000;
};

/** What an answer cost, from the model's prices and the tokens the service counted; null when either is not known */
export const costOfUsage = (option: Pick<TextModelOption, 'inputUsd' | 'outputUsd'> | null | undefined, answer: Pick<TextAnswer, 'inputTokens' | 'outputTokens'>): number | null => {
    if (!option || option.inputUsd === null || option.outputUsd === null || answer.inputTokens === null || answer.outputTokens === null) return null;
    return (answer.inputTokens * option.inputUsd + answer.outputTokens * option.outputUsd) / 1_000_000;
};

/** "<1¢", "2¢", "$0.15": the short form for a chip */
export const formatCostShort = (usd: number): string => {
    if (usd < 0.005) return '<1¢';
    if (usd < 0.1) return `${Math.max(1, Math.round(usd * 100))}¢`;
    return `$${usd.toFixed(2)}`;
};

/** "less than a cent", "about 2 cents", "about $0.15" */
export const formatCost = (usd: number): string => {
    if (usd < 0.005) return 'less than a cent';
    if (usd < 0.1) return `about ${Math.max(1, Math.round(usd * 100))} cent${Math.round(usd * 100) === 1 ? '' : 's'}`;
    return `about $${usd.toFixed(2)}`;
};

/** "$4.80 in · $24 out per 1M tokens" */
export const formatPrices = (option: Pick<TextModelOption, 'inputUsd' | 'outputUsd'>): string | null => {
    if (option.inputUsd === null || option.outputUsd === null) return null;
    const money = (usd: number) => (usd < 1 ? `$${usd.toFixed(2)}` : `$${Number(usd.toFixed(2))}`);
    return `${money(option.inputUsd)} in · ${money(option.outputUsd)} out per 1M tokens`;
};

// ── What the Ideas page asks for ─────────────────────────────────────────────

const BOARD_SYSTEM = 'You help a small film and video team think on a board of cards. Write in plain, concrete words. No preamble, no headings, no markdown.';

/** How the answer is to be shaped: a name line, then the words; the name is what the card is known by */
const NAMED = 'Begin with one line holding a name of two to five words for the card, then a line break, then the answer itself.';

/** The request a Think up card makes: whatever was written in it, with the kept cards to build on */
export const thinkRequest = (prompt: string, kept: string[]): TextRequest => {
    const context = kept.length > 0 ? `\n\nThe team has kept these cards so far; build on them, do not repeat them:\n${kept.map(text => `- ${text.replace(/\n+/g, ' ')}`).join('\n')}` : '';
    return {
        system: BOARD_SYSTEM,
        prompt: `${NAMED}\n\n${prompt.trim() || 'Give one idea that would make a good short film.'}${context}`,
        // Room for a whole short script (a two-minute screenplay is about this long), not only a few lines
        maxTokens: 2000,
    };
};

/** The request for a name for one card */
export const nameRequest = (text: string): TextRequest => ({
    system: BOARD_SYSTEM,
    prompt: `Give this card a name of two to five words to know it by on a board. Answer with the name only.\n\n${text}`,
    maxTokens: 30,
    temperature: 0.5,
});

export type CardTool = 'expand' | 'shorten' | 'rewrite' | 'variations';

export const CARD_TOOLS: { id: CardTool; label: string; hint: string }[] = [
    { id: 'expand', label: 'Expand', hint: 'A fuller version, about twice as long, as a new card' },
    { id: 'shorten', label: 'Shorten', hint: 'Its essence, about half as long, as a new card' },
    { id: 'rewrite', label: 'Rewrite', hint: 'The same idea said a fresh way, as a new card' },
    { id: 'variations', label: 'Three takes', hint: 'Three different takes on it, in one new card' },
];

/** The request for a tool used on one card: one answer, one card */
export const cardToolRequest = (tool: CardTool, text: string): TextRequest => {
    const asks: Record<CardTool, string> = {
        expand: 'Expand this into a fuller version, about twice as long, keeping its voice and intent.',
        shorten: 'Shorten this to its essence, about half as long, keeping its voice.',
        rewrite: 'Say this idea a fresh way: same intent, different words and angle.',
        variations: 'Give three different takes on this idea, each a paragraph of its own with a blank line between them.',
    };
    return { system: BOARD_SYSTEM, prompt: `${asks[tool]} ${NAMED}\n\n${text}`, maxTokens: tool === 'variations' ? 1000 : 700, temperature: tool === 'shorten' ? 0.5 : 0.9 };
};

// ── Screenplays ──────────────────────────────────────────────────────────────

const SCREENPLAY_SYSTEM = [
    'You are a professional screenwriter. You write screenplays in Fountain, the plain-text screenplay format, and nothing else: no preamble, no notes, no headings of your own, no markdown, no code fences.',
    'Fountain: a scene heading is a line in capitals beginning with INT. or EXT. (INT./EXT. for both), then the place, a hyphen, and the time of day, with a blank line before and after it.',
    'Action is plain prose in the present tense, in paragraphs separated by blank lines; sounds and important objects may be in capitals.',
    'A character who speaks has their name in capitals on a line of its own, with a blank line before it; what they say follows on the next lines, with no blank line between; a direction like (quietly) sits on its own line in parentheses right after the name. A blank line ends the speech.',
    'A transition such as CUT TO: is in capitals on its own line, ending with TO:, with a blank line before and after.',
    'Industry standard: one page is about a minute of screen time, about 55 lines. Keep to the length asked for.',
].join(' ');

export type ScreenplayMode = 'write' | 'rewrite' | 'continue' | 'edit';

/**
 * The request for a screenplay: written whole from a brief (with its title page), one scene
 * rewritten as asked (only that scene comes back), continued from its end, or changed as asked
 * in the writer's own words, the whole script coming back with the change made (the editor
 * then shows what changed, to keep or undo).
 */
export const screenplayRequest = (mode: ScreenplayMode, input: { brief: string; pages: number; title?: string; author?: string; text?: string; sceneHeading?: string; sceneText?: string; selection?: string }): TextRequest => {
    const pages = Math.min(20, Math.max(1, Math.round(input.pages || 2)));
    const tokens = Math.min(8, pages) * 1000 + 300;
    if (mode === 'edit') {
        // The whole script is the model's context, cheap to read; what it writes back is only the
        // scenes it changes, each under its [[Scene N]] note, which the app puts in their places
        const chosen = input.selection?.trim();
        const head = [input.title?.trim() ? `Title: ${input.title.trim()}` : null, input.author?.trim() ? `Written by ${input.author.trim()}` : null].filter(Boolean).join('\n');
        return {
            system: SCREENPLAY_SYSTEM,
            prompt: `Here is a screenplay in Fountain, a [[Scene N]] note before each scene:\n\n${head ? `${head}\n\n` : ''}${numberedBody(input.text ?? '')}\n\nMake this change: ${input.brief.trim() || 'Make it tighter and more vivid.'}${chosen ? `\n\nThe lines in question:\n${chosen}` : ''}\n\nAnswer with ONLY the scenes you change: each one complete, in Fountain, beginning with its [[Scene N]] note exactly as given and then its heading. Leave out every scene you do not change, and within a scene you change keep every line the change does not touch word for word. A new scene goes in the chunk of the scene it follows, after that scene's lines; a change before the first scene goes under [[Opening]]. Nothing before, between or after the scenes.`,
            // Room for the scenes that change, and for what is added; a cap, not a spend
            maxTokens: Math.min(16000, Math.max(1500, Math.round((input.text?.length ?? 0) / 3) + 1200)),
            temperature: 0.7,
        };
    }
    if (mode === 'rewrite') {
        return {
            system: SCREENPLAY_SYSTEM,
            prompt: `Here is a screenplay in Fountain:\n\n${input.text ?? ''}\n\nRewrite the scene "${input.sceneHeading ?? ''}" as asked below, keeping it in step with the rest of the script. Answer with ONLY the rewritten scene in Fountain, beginning with its scene heading, nothing before or after it.\n\nAsked: ${input.brief.trim() || 'Make it tighter and more vivid.'}`,
            maxTokens: Math.max(1500, Math.min(6000, Math.round(((input.sceneText?.length ?? 2000) / 3) * 2) + 800)),
            temperature: 0.85,
        };
    }
    if (mode === 'continue') {
        return {
            system: SCREENPLAY_SYSTEM,
            prompt: `Here is a screenplay in Fountain, unfinished:\n\n${input.text ?? ''}\n\nContinue it from where it ends, about ${pages} more page${pages === 1 ? '' : 's'}. ${input.brief.trim() ? `Direction: ${input.brief.trim()}` : ''}\n\nAnswer with ONLY the new material in Fountain, beginning with the next scene heading or the next line of action, nothing before it and no repetition of what is already written.`,
            maxTokens: tokens,
            temperature: 0.9,
        };
    }
    const titleLines = [input.title?.trim() ? `Title: ${input.title.trim()}` : null, input.author?.trim() ? `Credit: Written by\nAuthor: ${input.author.trim()}` : null].filter(Boolean).join('\n');
    return {
        system: SCREENPLAY_SYSTEM,
        prompt: `Write a complete screenplay of about ${pages} page${pages === 1 ? '' : 's'} (${pages} minute${pages === 1 ? '' : 's'} of screen time) from this brief:\n\n${input.brief.trim() || 'A short film of your own invention.'}\n\nBegin with a Fountain title page${titleLines ? ` of these lines, then a blank line:\n${titleLines}` : ' (Title:, Credit: Written by, Author:), then a blank line'}, then FADE IN: and the script. End with FADE OUT.`,
        maxTokens: tokens,
        temperature: 0.9,
    };
};
